import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { OpenApiClient } from '../api/openapi-client.service';
import { OrganizationContextService } from '../context/organization-context.service';
import { API_BASE_URL } from './api.config';
import {
  AuthUser,
  ChangePinRequest,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  LoginCredentials,
  PinLoginCredentials,
  ResetPasswordRequest,
} from './auth.models';
import { AuthSessionStore } from './auth-session.store';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly api = inject(OpenApiClient);
  private readonly apiBaseUrl = inject(API_BASE_URL).replace(/\/$/, '');
  private readonly session = inject(AuthSessionStore);
  private readonly organizationContext = inject(OrganizationContextService);
  private restoreRequest?: Promise<boolean>;

  readonly user = this.session.user;
  readonly status = this.session.status;
  readonly authenticated = this.session.authenticated;

  restoreSession(): Promise<boolean> {
    if (this.session.authenticated()) {
      return Promise.resolve(true);
    }

    if (this.session.status() === 'unauthenticated') {
      return Promise.resolve(false);
    }

    this.organizationContext.reset();
    this.restoreRequest ??= this.api
      .request('identity.auth.me', {})
      .then((response) => {
        this.organizationContext.reset();
        this.session.setUser(response.data);
        return true;
      })
      .catch(() => {
        this.organizationContext.reset();
        this.session.clear();
        return false;
      });

    return this.restoreRequest;
  }

  async login(credentials: LoginCredentials): Promise<AuthUser> {
    this.organizationContext.reset();
    await this.ensureCsrfCookie();

    const response = await this.api.request('authentication.login', { body: credentials });

    this.restoreRequest = undefined;
    this.organizationContext.reset();
    this.session.setUser(response.data);

    return response.data;
  }

  async loginWithPin(credentials: PinLoginCredentials): Promise<AuthUser> {
    this.organizationContext.reset();
    await this.ensureCsrfCookie();

    const response = await this.api.request('authentication.pinLogin', { body: credentials });

    this.restoreRequest = undefined;
    this.organizationContext.reset();
    this.session.setUser(response.data);

    return response.data;
  }

  async forgotPassword(request: ForgotPasswordRequest): Promise<string> {
    await this.ensureCsrfCookie();
    const response = await this.api.request('passwordReset.forgot', { body: request });

    return response.message;
  }

  async resetPassword(request: ResetPasswordRequest): Promise<string> {
    await this.ensureCsrfCookie();
    const response = await this.api.request('passwordReset.reset', { body: request });

    return response.message;
  }

  async changePassword(request: ChangePasswordRequest): Promise<string> {
    await this.ensureCsrfCookie();
    const response = await this.api.request('identity.auth.change-password', { body: request });

    this.restoreRequest = undefined;
    this.organizationContext.reset();
    this.session.clear();

    return response.message;
  }

  async changePin(request: ChangePinRequest): Promise<string> {
    await this.ensureCsrfCookie();
    const response = await this.api.request('identity.auth.change-pin', { body: request });

    return response.message;
  }

  async logout(): Promise<void> {
    this.organizationContext.reset();

    try {
      await this.api.request('identity.auth.logout', {});
    } finally {
      this.restoreRequest = undefined;
      this.organizationContext.reset();
      this.session.clear();
    }
  }

  private url(path: string): string {
    return `${this.apiBaseUrl}${path}`;
  }

  private ensureCsrfCookie(): Promise<void> {
    return firstValueFrom(this.http.get<void>(this.url('/sanctum/csrf-cookie')));
  }
}
